import { zodResolver } from '@hookform/resolvers/zod';
import { ListX } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm, useWatch, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { InterviewOut } from '@/api/interviews';
import { isRecommendation, RECOMMENDATIONS } from '@/api/vocabularies';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { Eyebrow } from '@/components/ui/Eyebrow';
import { RadioGroup } from '@/components/ui/RadioGroup';
import { Textarea } from '@/components/ui/Textarea';
import { cn } from '@/lib/cn';
import { type } from '@/theme/typography';

import { fillingInSchema, formFieldsOf, type FillingInForm } from './fillingInFields';
import { QuestionBlock } from './QuestionBlock';
import { SaveIndicator, SaveNotice } from './SaveIndicator';
import { SubmitDialog } from './SubmitDialog';
import {
  isRefused,
  NOTHING_REFUSED,
  problemFieldId,
  problemFor,
  problemMessageKey,
  revealProblem,
  refusalInForm,
  type Refusal,
} from './submissionProblems';
import { useAutosave } from './useAutosave';

/**
 * ⚠️ **A real copy of the form's values, and it is a fix rather than a precaution — found in a live
 * browser on 2026-07-30 (TASK-088).**
 *
 * `react-hook-form`'s `getValues()` returns a **shallow** spread of its internal `_formValues`
 * (`{...(_state.mount ? _formValues : _defaultValues)}` — read out of `react-hook-form@7.66.1`), so
 * `scores` and every object in it are **shared by reference** with the live form. The refusal below is
 * held *with the values it was about* precisely so each block can ask *"has the reader changed this
 * field since?"* — and against a shared reference that question always answers *no*: the snapshot moved
 * with the keystrokes. **Measured in the browser:** a refused comment was typed in full and the message
 * and `aria-invalid` stayed on it. The jsdom suite did not reproduce it, which is why this line exists
 * and why the live pass is a criterion rather than a courtesy.
 */
const snapshotOf = (values: FillingInForm): FillingInForm => structuredClone(values);

interface Props {
  /** The reader's **own** unsubmitted interview — the branch in `index.tsx` is what guarantees it. */
  interview: InterviewOut;
  /** The viewer: the autosave's cache key and §6.7's addendum rule are both keyed on them. */
  viewerId: number;
}

/**
 * §6.6 — **the working surface people sit in for an hour.** `max-w-3xl` (the shell's), no modal, no
 * collapse, a sticky header, one block per score in the order the payload sends them, and hairlines
 * between them rather than cards.
 *
 * ⚠️ **Three of the seven feedback protections are rendered here, and the design constraint that
 * carries all three is a *negative* one: nothing on this surface may anchor the opinion being
 * written.**
 *
 *  * **#1, the question-text snapshot (ADR 0004)** — see `QuestionBlock`, which renders
 *    `question_text_snapshot` and cannot reach the live wording even if it tried.
 *  * **#2, archive instead of delete (ADR 0005)** — `interview.scores` is mapped and **never filtered,
 *    sliced or sorted**, here or in `fillingInFields.ts`. A score whose bank question has since been
 *    archived is part of *this* conversation and stays rendered and fillable; the backend keeps it
 *    writable on purpose (TASK-031) and the client's whole duty is not to hide it. This is the
 *    obligation Sprint 10's TASK-078 recorded as the one thing it could not verify.
 *  * **#3, others' feedback hidden until yours is submitted (ADR 0006)** — this screen renders **no
 *    other interviewer's feedback, no aggregate and no count of who else has submitted**, and it asks
 *    for nothing that could carry one. **One** read reaches the server from here:
 *    `GET /api/v1/interviews/{id}/`, the screen's own, one level up — the header's candidate name comes
 *    off that payload since backend TASK-107 (see the note on the header below), and the person card,
 *    which carries the aggregates, is not fetched at all. `GET /api/v1/interviews/?person_id=` is
 *    **not** called here either; that block is §6.7's.
 *
 * ⚠️ **The one thing this screen writes is the draft, and it writes it by itself** — feedback protection
 * #4 (ADR 0007), added by TASK-083: `useAutosave` debounces the form onto
 * `PATCH /api/v1/interviews/{id}/`, `SaveIndicator` renders §6.6's states in the header and `SaveNotice`
 * answers the two 409s and the one navigation that can lose something. Until TASK-083 landed this file
 * said it saved nothing, and the two shipped in **one release**, because a form that looks like it saves
 * and does not is precisely the loss ADR 0007 exists to prevent.
 *
 * ⚠️ **The one *irreversible* thing it does is submit, and that is TASK-084's** — protection #5 (ADR
 * 0008): `SubmitDialog` holds the confirmation and the whole of `POST …/submit/`, and this component
 * holds the completeness gate's answer, because the messages belong at the fields. Two properties of it
 * are decided here rather than in the dialog: **Submit is never disabled** (see `askToSubmit`), and the
 * autosave is **flushed** before anything is frozen — submission sends no body, so it freezes what the
 * server holds, and the debounce means that is up to two seconds behind the screen.
 *
 * ⚠️ **No control on this surface says *Save*, now or ever** (ADR 0007: *"do not add a Save button as
 * reassurance — a Save button teaches people that not pressing it loses work, which is the anxiety
 * autosave was built to remove"*). `fillingIn.test.tsx` sweeps every control and every string on the
 * rendered screen for `/save/i`, so the day one arrives it is a deliberate edit and not a kindness.
 */
export function FillingIn({ interview, viewerId }: Props) {
  const { t } = useTranslation(['interviews', 'components']);

  /*
   * ⚠️ **The candidate's name used to be a second request, and backend TASK-107 removed the reason.**
   * `ConsiderationOnInterviewOut` was `{id, person_id, role_id, role_title, status}`, so §6.6's
   * *"Candidate, vacancy"* header had to fetch the **person card** — a payload carrying the average, both
   * counters, the latest recommendation and every other interview of that person, i.e. exactly what §6.6
   * forbids on screen. It was safe (the viewer necessarily holds an unsubmitted interview for that
   * person, so the server's own gate had already nulled every colleague's verdict) and it was a whole
   * card fetched for one string, with a rendering rule as the only thing standing between the two.
   *
   * The payload carries the name now, so there is no second request, no second loading state, and no
   * rendering rule to enforce — `interviews.test.tsx` asserts the screen makes one request for its
   * header, which is the assertion the old arrangement could not make.
   */

  const form = useForm<FillingInForm>({
    resolver: zodResolver(fillingInSchema),
    /*
     * The payload as it stands **is** the default, and no effect resets the form afterwards: a draft
     * refetched under a half-typed comment would otherwise overwrite what somebody is in the middle of
     * writing, which is the failure this whole screen exists to avoid. The component is mounted per
     * interview id (the address), so a different interview is a different mount.
     */
    defaultValues: formFieldsOf(interview),
  });

  /**
   * ⚠️ **Feedback protection #4 lives on this line** (ADR 0007): the draft is on the server within
   * seconds of being typed, on a debounce, and in no browser storage at all. The hook holds the whole
   * engine — one request in flight, a queue behind it, a bounded retry, the two 409s and the one
   * navigation worth blocking — and hands this component six states to render.
   *
   * It is called **here**, one level above the question list, and the trade is deliberate: a status
   * change re-renders the list (twice per save — *Saving…*, then *Saved*), while a **keystroke** still
   * re-renders nothing but its own block, because the hook watches the form through a subscription
   * rather than through state. The per-keystroke path is the one that had to stay cheap on a form with
   * forty questions on it; two re-renders per pause in the typing is not a cost worth a second copy of
   * the status somewhere else.
   */
  const autosave = useAutosave({ interview, viewerId, form });

  /**
   * ⚠️ **The completeness gate's last answer, held once for the whole screen** (TASK-084) — either the
   * server's `422` or the client's own pre-check of the same three rules, in the same shape, because the
   * fields do not care which of the two refused.
   *
   * ⚠️ **It is held *with the values it was about*, and that pairing is the whole mechanism.** A refusal
   * is an answer about a particular state of the form, so each message stands until the reader **changes
   * that field** — which each block decides by comparing its own watched value with this snapshot
   * (`QuestionBlock`, `OverallBlock`). Two alternatives were rejected: re-checking the rules on every
   * render would mean watching every field here, one re-render of a forty-question list per keystroke;
   * and hiding a message as soon as the *local* value satisfies the *local* copy of the rule would throw
   * the server's answer away in the one case worth rendering, the two disagreeing.
   */
  const [refused, setRefused] = useState<{ refusal: Refusal; values: FillingInForm } | null>(null);
  const [confirming, setConfirming] = useState(false);

  const refusal = refused?.refusal ?? NOTHING_REFUSED;

  /**
   * §6.6: *"Missing required comments block submission, with the fields scrolled into view."* — the first
   * problem, scrolled to and focused.
   *
   * ⚠️ **In an effect, and that is a measured requirement rather than a preference.** The server's refusal
   * arrives while the confirmation is still open, and closing a Radix dialog **restores focus to the
   * control that opened it** (`Dialog`'s own `onCloseAutoFocus`, added by TASK-069's live pass). Focusing
   * the field from the handler therefore put the cursor on the field and then had it taken back to
   * *Submit*. **One frame late, for the reason `Dialog` records in the same words**: Radix restores focus
   * on its own unmount and does it asynchronously, so an effect alone still loses the race — measured,
   * and the frame is what makes the field keep the focus. The client's own pre-check goes through this
   * same path, so there is one behaviour and not two.
   */
  useEffect(() => {
    if (refused === null || refused.refusal.fields.length === 0) return undefined;

    const field = refused.refusal.fields[0].field;
    const frame = requestAnimationFrame(() => revealProblem(field));
    return () => cancelAnimationFrame(frame);
  }, [refused]);
  /** The comment this question held when the refusal arrived, by `question_id` and never by index. */
  const commentAtRefusal = (questionId: number) =>
    refused?.values.scores.find((score) => score.question_id === questionId)?.comment;

  /**
   * ⚠️ **Submit is never disabled** (BUG-020's shape: a disabled primary action with no sentence saying
   * why), so pressing it on an unfinished interview has to answer. The client checks the server's three
   * rules first — `refusalInForm` — and if it finds any, **no request is made and no confirmation opens**:
   * asking *"are you sure? this cannot be undone"* about an act that is about to be refused is a question
   * with a wrong answer either way. The fields are marked and the first one is scrolled to and focused.
   *
   * ⚠️ **The server is still the authority**, and that is why the local copy is a courtesy rather than a
   * gate: when it finds nothing, the confirmation opens and the submission goes out — and if the server
   * refuses it anyway, `onRefused` below renders that answer exactly the same way. A client that trusted
   * its own copy of the rules is the failure mode both paths exist to avoid, and they are tested
   * separately.
   */
  const askToSubmit = () => {
    const values = form.getValues();
    const found = refusalInForm(values);

    if (isRefused(found)) {
      setRefused({ refusal: found, values: snapshotOf(values) });
      return;
    }

    // Nothing local to answer: the last refusal, if there was one, is cleared and the question is asked.
    setRefused(null);
    setConfirming(true);
  };

  return (
    <>
      {/*
       * §6.6's **sticky** header: candidate and vacancy on the left, so a reader forty questions down
       * still knows whose conversation this is, and **the save indicator on the right** — the one place
       * on this screen that says anything about saving (Submit joins it with TASK-084).
       */}
      <header className="sticky top-0 z-10 border-b border-rule bg-paper-bg pb-3 pt-4">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            {/*
              ⚠️ **The candidate's name comes off this screen's own payload since backend TASK-107** —
              there is no second request and therefore no second loading state. It cost a `personQuery`
              per interview screen, which was one request for one string that the interview payload could
              carry; the two shapes ADR 0006 defines are what keep it safe (a viewer who may not read
              this feedback receives one with no consideration object at all).
            */}
            <p className={type.sectionHeading}>{interview.consideration.person_full_name}</p>
            <p className={cn(type.metadata, 'mt-1')}>{interview.consideration.role_title}</p>
          </div>

          {/*
           * §6.6's header: *"the save indicator and Submit"*, in that order and on one line. Submit is the
           * primary action of the screen and it is **never disabled** — see `askToSubmit`.
           *
           * ⚠️ **Below `lg` the same pair is a bar fixed to the bottom of the window** — §10: *"the
           * interview screen becomes single-column with the save indicator and Submit fixed to the
           * bottom"* — which the screen did not do until BUG-024. Two documents disagreed and §10 is the
           * one that wins: it is the responsive spec, §6.6 describes the desktop layout, and on a form
           * somebody scrolls for an hour on a phone the primary action belongs where the thumb is.
           *
           * ⚠️ **It is one element repositioned, never two rendered.** A second copy behind
           * `lg:hidden` / `hidden lg:flex` would put **two `aria-live` regions** for the same indicator
           * in the document, and a screen reader would announce every save twice — which is the shape of
           * defect this screen has already paid for once (§4's `Textarea`, green for four sprints). So
           * the container is `fixed` below `lg` and `lg:static` above it: same node, same live region,
           * two positions.
           */}
          <div
            className={cn(
              'fixed inset-x-0 bottom-0 z-20 flex items-center justify-end gap-3',
              'border-t border-rule bg-paper-bg px-4 py-3',
              'lg:static lg:inset-auto lg:z-auto lg:shrink-0 lg:border-0 lg:bg-transparent lg:p-0',
            )}
          >
            <SaveIndicator autosave={autosave} />
            <Button size="sm" onClick={askToSubmit}>
              {t('interviews:filling.submit.action')}
            </Button>
          </div>
        </div>

        {/*
         * The two states that are a paragraph rather than an indicator — the autosave's two 409s and a
         * navigation that would lose something. They sit **inside the sticky header** so that neither can
         * be scrolled away from on a screen that is forty questions long, and full width because each is
         * a sentence with an action under it.
         */}
        <SaveNotice autosave={autosave} />

        {/*
         * ⚠️ **The refusal's one announcement, and the messages themselves are at the fields.** A refused
         * submission has to be *said* — a reader who pressed Submit and saw the page not change would
         * conclude it was submitted — but it must not be said as a list, because a list in a header is
         * something to remember while the fix is somewhere else. So: one sentence here, `role="alert"` so
         * a screen reader hears it, and the inline messages do the pointing (§4, §9). It carries **no
         * count**, deliberately: the blocks clear their own messages as they are fixed, and a number in
         * the header would go stale the moment the first one does.
         */}
        {isRefused(refusal) && (
          <div className="mt-3" role="alert">
            <p className={cn(type.metadata, 'text-negative')}>
              {t('interviews:filling.submit.problem.summary')}
            </p>
            {refusal.unplaced.length > 0 && (
              <p className={cn(type.metadata, 'mt-1 text-negative')}>
                {t('interviews:filling.submit.problem.elsewhere')}
              </p>
            )}
          </div>
        )}
      </header>

      {/*
       * ⚠️ **An interview with zero scores is a real state, not a defensive branch** (§8, and §6.6
       * specified none): a vacancy whose bank was empty or entirely archived when the interview was
       * scheduled freezes in **no** questions, and the backend answers `201` for it on purpose
       * (TASK-028). TASK-081's dialog says the first half of this sentence at scheduling time; this is
       * the other half, and the Overall block below still renders, because the verdict and the comment
       * stand on their own.
       */}
      {interview.scores.length === 0 ? (
        <EmptyState className="mt-8" icon={ListX} message={t('interviews:filling.noQuestions')} />
      ) : (
        <>
          <Eyebrow className="mt-8" count={interview.scores.length}>
            {t('interviews:filling.questions')}
          </Eyebrow>
          {/*
           * An ordered list, hairline-separated (§6.6: *"separated by hairlines — not cards"*), in the
           * payload's own order. Nothing here reorders it and nothing offers to: the order **is** the
           * frozen bank order (ADR 0004) and a control that changed it would be a defect.
           */}
          <ol className="divide-y divide-rule">
            {interview.scores.map((score, index) => (
              <QuestionBlock
                key={score.id}
                form={form}
                index={index}
                score={score}
                problem={problemFor(refusal, { kind: 'score', questionId: score.question_id })}
                commentAtRefusal={commentAtRefusal(score.question_id)}
              />
            ))}
          </ol>
        </>
      )}

      {/*
       * The bottom padding is the other half of the fixed bar (BUG-024): without it the bar covers the
       * end of the Overall block on a phone — the comment somebody is typing — and the screen would have
       * traded a reachable Submit for an unreachable field. `lg:pb-0` because above `lg` the controls are
       * back in the header and there is nothing at the bottom to clear.
       */}
      <section className="mt-10 pb-24 lg:pb-0">
        <Eyebrow>{t('interviews:filling.overall')}</Eyebrow>
        <OverallBlock form={form} refusal={refusal} valuesAtRefusal={refused?.values} />
      </section>

      {/*
       * Mounted only while it is open, so a cancelled confirmation leaves nothing behind — the shape
       * §6.5's dialogs established, and the reason a second press starts from the question again.
       */}
      {confirming && (
        <SubmitDialog
          interview={interview}
          viewerId={viewerId}
          autosave={autosave}
          onRefused={(found) =>
            setRefused({ refusal: found, values: snapshotOf(form.getValues()) })
          }
          onClose={() => setConfirming(false)}
        />
      )}
    </>
  );
}

/**
 * §6.6's **Overall** block: the four verdicts and the comment, after the questions.
 *
 * It is a component of its own so that typing in the overall comment re-renders this block rather than
 * every question above it — the same reason `QuestionBlock` watches its own two fields.
 *
 * ⚠️ **Four options and no fifth.** There is no *undecided* and no way back to one: the API can neither
 * clear the verdict (an explicit `null` is a 422) nor set it blank (`''` is not one of the four), so
 * offering to un-decide would be offering something no request could carry (`api/interviews.ts`). An
 * undecided draft is simply one with nothing chosen, which is where every draft starts.
 */
function OverallBlock({
  form,
  refusal,
  valuesAtRefusal,
}: {
  form: UseFormReturn<FillingInForm>;
  refusal: Refusal;
  /** The two values as they were when the refusal arrived — see `FillingIn`'s note on the pairing. */
  valuesAtRefusal?: FillingInForm;
}) {
  const { t } = useTranslation(['interviews', 'components']);

  /**
   * Both ids are derived rather than generated, for `QuestionBlock`'s reason: a refusal names these two
   * fields by `loc`, and the scroll-and-focus target has to be findable from the refusal alone.
   */
  const verdictId = problemFieldId({ kind: 'overall_recommendation' });
  const commentId = problemFieldId({ kind: 'overall_comment' });

  const verdict = useWatch({ control: form.control, name: 'overall_recommendation' });
  const comment = useWatch({ control: form.control, name: 'overall_comment' });

  const registered = form.register('overall_comment');

  /** As in `QuestionBlock`: each message stands until the reader changes the field it is about. */
  const verdictProblem =
    valuesAtRefusal !== undefined && verdict === valuesAtRefusal.overall_recommendation
      ? problemFor(refusal, { kind: 'overall_recommendation' })
      : undefined;
  const commentProblem =
    valuesAtRefusal !== undefined && comment === valuesAtRefusal.overall_comment
      ? problemFor(refusal, { kind: 'overall_comment' })
      : undefined;

  return (
    <>
      <p className="text-xs font-medium text-ink-700">{t('interviews:filling.recommendation')}</p>
      {verdictProblem && (
        <p className={cn(type.metadata, 'mt-1 text-negative')}>
          {t(problemMessageKey(verdictProblem))}
        </p>
      )}
      <RadioGroup
        id={verdictId}
        className="mt-2 flex-wrap gap-x-6 gap-y-3"
        label={t('interviews:filling.recommendation')}
        /*
         * The values are the document's (`RECOMMENDATIONS`, derived from the OpenAPI enum) and the words
         * are the catalogue's, so `STRONG_YES` is never what a person reads and a verdict added on the
         * backend appears here without an edit.
         */
        options={RECOMMENDATIONS.map((value) => ({
          value,
          label: t(`components:recommendation.${value}`),
        }))}
        value={verdict}
        onValueChange={(next) => {
          // The derived guard, never a cast: a value this screen cannot name is one it does not store.
          if (isRecommendation(next)) {
            form.setValue('overall_recommendation', next, { shouldDirty: true });
          }
        }}
      />

      {/*
       * The provenance rule (§6.6): it marks **authorship**, so it goes on every piece of the
       * interviewer's own writing — this comment as much as a per-question one — and on no control. The
       * verdict above is a `RadioGroup` with borders of its own, and a second rule beside it would only
       * muddle them.
       */}
      <div className="mt-6 flex flex-col gap-1 border-l border-rule pl-4">
        <label htmlFor={commentId} className="text-xs font-medium text-ink-700">
          {t('interviews:filling.overallComment')}
        </label>
        {commentProblem && (
          <p id={`${commentId}-problem`} className={cn(type.metadata, 'text-negative')}>
            {t(problemMessageKey(commentProblem))}
          </p>
        )}
        {/*
         * Controlled from the form's own value, like every other comment on this screen: the primitive
         * grows itself when its `value` changes, and it receives `react-hook-form`'s ref through the
         * spread — the pair TASK-078's fix made work together.
         */}
        <Textarea
          {...registered}
          id={commentId}
          value={comment}
          aria-invalid={commentProblem !== undefined || undefined}
          aria-describedby={commentProblem ? `${commentId}-problem` : undefined}
        />
      </div>
    </>
  );
}
